import axios from 'axios';
import { throttleAdapterEnhancer } from 'axios-extensions';
import axiosRetry from 'axios-retry';

import { getDeviceId } from 'shared-services/device-service';
import { logError, remoteLog } from 'shared-services/logger-service';

import config from '../config';
import { showModal } from '../actions/modal';
import { getMasterToken, getTeamToken, getCurrentTeam } from './store-service';
import { getPublicToken } from './public-share-service';
import { logout } from '../globals-service';
import { browserHistory } from './history';
import { getVersion } from './version-service';
import { createToast } from './alert-service';

const KNOWN_ERROR_CODES = [503, 440, 520, 441];
const NETWORK_CONNECTION_ERRORS = ['timeout of 0ms exceeded', 'Network Error'];

const axiosHttp = axios.create({
    adapter: throttleAdapterEnhancer(axios.defaults.adapter, { threshold: 1000 })
});

axiosRetry(axiosHttp, {
    retries: 3,
    retryDelay: retryCount => retryCount * 1000
});

let onNetworkError = () => {};

export const createRequestUrl = (url, apiService, options) => {
    let host = config.HOST;

    if (options?.useAltAPI) {
        host = config.ALT_HOST;
    }

    switch (apiService) {
        case 'messages':
            if (options?.useMessagesService) {
                if (options?.useAltAPI) {
                    host = config.ALT_MESSAGES_HOST;
                } else {
                    host = config.MESSAGES_HOST;
                }
            }
            break;

        default:
            break;
    }

    return `${host}/${url}`;
}

export const validateResponse = (response, key) => {
    if (!response?.data?.[key]) {
        throw new Error('Invalid response');
    }

    return response;
}

export default function AjaxService(requestConfig, useMasterToken, token) {
    const team = getCurrentTeam();
    const authToken = token || (useMasterToken ? getMasterToken() : (getPublicToken() || getTeamToken()));

    const handleError = error => {
        if (error.field === 'x-session-token' || error.field === 'url') {
            return true;
        }

        switch ((error.response && error.response.type) || error.type) {
            case 'TeamMembersLimitError':
                showModal('SWITCH_PLAN', {
                    upgrade: 'members'
                });

                return true;

            case 'ProjectsLimitError':
                showModal('SWITCH_PLAN', {
                    upgrade: 'projects'
                });

                return true;

            case 'TimeFileLimitError':
                showModal('SWITCH_PLAN', {
                    upgrade: 'storageBytes'
                });

                return true;

            default:
                // eslint
        }

        if (error?.response?.data?.message === 'Password changed, please login to connect') {
            createToast('error', error?.response?.data?.message);
            logout();

            return true;
        }

        if (error.response && error.response.status) {
            switch (error.response.status) {
                // case 503:
                case 440:
                case 520:
                    navigateToMaintenance(error.response.status);
                    throw error;

                case 441:
                    logout();
                    throw error;

                default:
            }
        }

        return false;
    }

    if (!requestConfig.hasOwnProperty('headers')) {
        requestConfig.headers = {};
    }

    if (!requestConfig.hasOwnProperty('params')) {
        requestConfig.params = {};
    }

    requestConfig.headers['Device-Id'] = getDeviceId();
    requestConfig.headers['APP-Version'] = getVersion();

    if (authToken) {
        requestConfig.headers['Authorization'] = `Bearer ${authToken}`;
    }

    if (requestConfig.data && requestConfig.data instanceof FormData) {
        requestConfig.headers['Content-Type'] = 'multipart/form-data; charset=utf-8';
    }

    requestConfig.url = requestConfig.url.indexOf('http') === 0 ? requestConfig.url : createRequestUrl(requestConfig.url, requestConfig.apiService, {
        useMessagesService: team?.feature_flags?.includes('UseSeparateMessagesService'),
        useAltAPI: team?.feature_flags?.includes('UseAltAPI')
    });

    const prepareForParams = (source) => {
        const result = {};

        Object.keys(source).forEach(key => {
            const value = source[key];
            if (typeof value !== 'undefined') {
                result[key] = value.toString();
            }
        });

        return result;
    }

    // const params = requestConfig.params ? `?${new URLSearchParams(prepareForParams(requestConfig.params))}` : '';

    // return fetch(requestConfig.url + params, {
    //     method: requestConfig.method,
    //     headers: requestConfig.headers,
    //     body: requestConfig.data ? JSON.stringify(requestConfig.data) : undefined
    // }).then(r => r.json()).then(data => ({ data }))

    return axiosHttp(requestConfig)
        .then(response => {
            // trackTiming({
            //     type: 'AJAX_LOAD',
            //     time: new Date() - ajaxStartTimestamp,
            //     label: requestConfig.url
            // });

            if (!response.data) {
                return Promise.reject({
                    ...response,
                    response: response.data ? {
                        ...response.data,
                        status: response.data.statusCode
                    } : {
                        status: 'no data'
                    },
                    silent: true
                });
            }

            return response;
        })
        .catch(error => {
            if (requestConfig.url.indexOf('/log') < 0) {
                // remoteLog(`NETWORK ERROR | XHR | ${error.code} | ${requestConfig.url}`);
            }

            if (NETWORK_CONNECTION_ERRORS.includes(error.message) && requestConfig.url.indexOf('/log') < 0) {

                if (requestConfig.url.indexOf('/me/events') >= 0) { 
                    // onNetworkError();
                }
            }

            if (handleError(error)) {
                return;
            }

            if (!error.__CANCEL__ && !(error.response && error.response.status && KNOWN_ERROR_CODES.includes(error.response.status))) {
                // if (typeof error?.response?.status !== 'undefined') {
                    logError(`xhr ${error?.response?.status}`, error, {
                        requestConfig,
                        response: error.response
                    });
                // }
            }

            throw error;
        });
}

let navigating = false;

export const setNetworkErrorHandler = handler => onNetworkError = handler;

const navigateToMaintenance = code => {
    if (navigating) {
        return;
    }

    browserHistory.push({
        pathname: `/maintenance`,
        state: { code }
    });

    navigating = setTimeout(() => {
        clearTimeout(navigating);
        navigating = false;
    }, 500);
}